import { useId, type ReactNode } from 'react';
import { Icone } from './Icone.tsx';

/**
 * Switch para o que vale imediatamente ao clicar — uma regra de envio, o
 * formulário aberto ou fechado. Para algo que só vale ao salvar, use `Caixa`:
 * o switch promete efeito instantâneo.
 */
export function Alternador({
  ligado,
  aoMudar,
  titulo,
  descricao,
  desabilitado,
}: {
  ligado: boolean;
  aoMudar: (v: boolean) => void;
  titulo: ReactNode;
  descricao?: ReactNode;
  desabilitado?: boolean;
}) {
  return (
    <label className="alternador">
      <input
        type="checkbox"
        role="switch"
        className="alternador__entrada"
        checked={ligado}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      <span className="alternador__trilho" aria-hidden="true">
        <span className="alternador__pino" />
      </span>
      <span className="alternador__texto">
        <span className="alternador__titulo">{titulo}</span>
        {descricao && <span className="campo__dica" style={{ display: 'block' }}>{descricao}</span>}
      </span>
    </label>
  );
}

/**
 * Escolha única entre poucas opções — "Sim/Não", sexo.
 *
 * É `radio` de verdade, não um `Select` de duas opções nem um par de
 * botões: com radio o leitor de tela anuncia "1 de 2", as setas do teclado
 * andam entre as opções e o navegador já entende o grupo. Reaproveita o
 * bloco clicável da `Escolha` para ter o mesmo alvo grande no celular.
 */
export function Opcoes<T extends string>({
  rotulo,
  opcoes,
  valor,
  aoMudar,
  obrigatorio,
  dica,
  desabilitado,
}: {
  rotulo: string;
  opcoes: { valor: T; rotulo: string }[];
  valor: T | null;
  aoMudar: (v: T) => void;
  obrigatorio?: boolean;
  dica?: ReactNode;
  desabilitado?: boolean;
}) {
  const id = useId();

  return (
    <div className="campo" role="radiogroup" aria-labelledby={`${id}-rotulo`}>
      <span className="campo__rotulo" id={`${id}-rotulo`}>
        {rotulo}
        {obrigatorio && (
          <span className="campo__obrigatorio" aria-hidden="true">
            *
          </span>
        )}
      </span>

      <div className="opcoes">
        {opcoes.map((o) => (
          <label key={o.valor} className="escolha escolha--opcao">
            <span className="caixa">
              <input
                type="radio"
                name={id}
                className="caixa__entrada"
                value={o.valor}
                checked={valor === o.valor}
                disabled={desabilitado}
                onChange={() => aoMudar(o.valor)}
              />
              <span className="caixa__ponto" aria-hidden="true" />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>{o.rotulo}</span>
          </label>
        ))}
      </div>

      {dica && <span className="campo__dica">{dica}</span>}
    </div>
  );
}

/** Bloco clicável inteiro, não só a caixinha — alvo maior no celular. */
export function Escolha({
  marcada,
  aoMudar,
  children,
  desabilitada,
}: {
  marcada: boolean;
  aoMudar: (v: boolean) => void;
  children: ReactNode;
  desabilitada?: boolean;
}) {
  return (
    <label className="escolha">
      <span className="caixa">
        <input
          type="checkbox"
          className="caixa__entrada"
          checked={marcada}
          disabled={desabilitada}
          onChange={(e) => aoMudar(e.target.checked)}
        />
        <Icone.Check tamanho={13} className="caixa__risco" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </label>
  );
}
